import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, mediaUrl, fmt } from "../lib/api";
import { Plus, Pencil, Trash2, LogOut, Package, ShoppingCart, DollarSign, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

const empty = { title: "", description: "", category: "ebooks", price: "", cover_path: "", file_path: "", file_name: "", featured: false };

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-zinc-500">{label}</span>
        <Icon className="w-4 h-4 text-zinc-600" />
      </div>
      <p className="font-head text-3xl mt-3 tracking-tight">{value}</p>
    </div>
  );
}

function Uploader({ label, accept, value, fileName, onUploaded, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.path, data.filename);
      toast.success(`${label} uploaded`);
    } catch {
      toast.error(`${label} upload failed`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <label className="block text-xs text-zinc-500 mb-1.5">{label}</label>
      <label className="flex items-center gap-3 bg-zinc-950 border border-dashed border-zinc-700 rounded-md px-3 py-3 cursor-pointer hover:border-zinc-500 transition-colors text-sm">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-zinc-400" />}
        <span className="text-zinc-400 truncate">{fileName || value ? (fileName || "Uploaded ✓") : "Choose file"}</span>
        <input data-testid={testid} type="file" accept={accept} onChange={upload} className="hidden" />
      </label>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  const [stats, setStats] = useState({ products: 0, orders: 0, revenue: 0 });
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const changePassword = async () => {
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setPwSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: pw.current_password, new_password: pw.new_password,
      });
      localStorage.setItem("vm_token", data.token);
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password updated — you're secured for launch");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update password");
    } finally {
      setPwSaving(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [s, p, o] = await Promise.all([
        api.get("/admin/stats"), api.get("/products"), api.get("/admin/orders"),
      ]);
      setStats(s.data); setProducts(p.data); setOrders(o.data); setLoaded(true);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("vm_token"); nav("/admin/login");
      }
    }
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem("vm_token")) { nav("/admin/login"); return; }
    api.get("/auth/me").then(load).catch(() => { localStorage.removeItem("vm_token"); nav("/admin/login"); });
  }, [load, nav]);

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditId(p.id); setForm({ ...empty, ...p, price: String(p.price) }); setOpen(true); };

  const save = async () => {
    if (!form.title || !form.description || !form.price || !form.cover_path || (!editId && !form.file_path)) {
      toast.error("Fill all fields and upload cover + product file");
      return;
    }
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) };
    try {
      if (editId) await api.put(`/admin/products/${editId}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editId ? "Product updated" : "Product created");
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted"); load();
  };

  const logout = () => { localStorage.removeItem("vm_token"); nav("/admin/login"); };

  return (
    <div className="App min-h-screen bg-[#09090b]">
      <header className="border-b border-zinc-800 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo-ddl.png" alt="Digital Dime Lab" className="w-6 h-6" />
            <span className="font-head font-semibold tracking-tight">Digital Dime Lab · Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="text-sm text-zinc-400 hover:text-white transition-colors">View store ↗</a>
            <button data-testid="logout-button" onClick={logout} className="inline-flex items-center gap-2 text-sm border border-zinc-800 hover:border-zinc-600 px-3 py-2 rounded-md transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={Package} label="Products" value={stats.products} />
          <StatCard icon={ShoppingCart} label="Paid orders" value={stats.orders} />
          <StatCard icon={DollarSign} label="Revenue" value={fmt(stats.revenue)} />
        </div>

        <div className="flex items-center gap-2 mt-10 border-b border-zinc-800">
          {["products", "orders", "security"].map((t) => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-sm capitalize border-b-2 -mb-px transition-colors ${tab === t ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
              {t}
            </button>
          ))}
          <div className="flex-1" />
          {tab === "products" && (
            <button data-testid="add-product-button" onClick={openNew}
              className="inline-flex items-center gap-2 bg-white text-black px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-200 transition-colors mb-2">
              <Plus className="w-4 h-4" /> New product
            </button>
          )}
        </div>

        {tab === "products" && (
          <div className="mt-6 border border-zinc-800 rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="products-table">
              <thead className="bg-zinc-950 text-zinc-500">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Product</th>
                  <th className="text-left font-medium px-4 py-3 hidden sm:table-cell">Category</th>
                  <th className="text-left font-medium px-4 py-3">Price</th>
                  <th className="text-right font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-zinc-800 hover:bg-zinc-900/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={mediaUrl(p.cover_path)} alt="" className="w-12 h-9 object-cover rounded border border-zinc-800" />
                        <span className="font-medium">{p.title}</span>
                        {p.featured && <span className="text-[10px] bg-white text-black px-1.5 py-0.5 rounded uppercase">Featured</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 capitalize text-zinc-400 hidden sm:table-cell">{p.category}</td>
                    <td className="px-4 py-3 font-mono">{fmt(p.price)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 text-zinc-400 hover:text-white transition-colors"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`delete-${p.id}`} onClick={() => del(p.id)} className="p-2 text-zinc-400 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {products.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-zinc-600">{loaded ? "No products yet." : "Loading…"}</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-6 border border-zinc-800 rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead className="bg-zinc-950 text-zinc-500">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Email</th>
                  <th className="text-left font-medium px-4 py-3 hidden sm:table-cell">Items</th>
                  <th className="text-left font-medium px-4 py-3">Amount</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-zinc-800 hover:bg-zinc-900/50">
                    <td className="px-4 py-3">{o.email}</td>
                    <td className="px-4 py-3 text-zinc-400 hidden sm:table-cell">{o.items?.map((i) => i.title).join(", ")}</td>
                    <td className="px-4 py-3 font-mono">{fmt(o.amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${o.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-700/40 text-zinc-400"}`}>
                        {o.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-zinc-600">No orders yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "security" && (
          <div className="mt-6 max-w-md" data-testid="security-panel">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
              <h3 className="font-head text-lg">Change admin password</h3>
              <p className="text-sm text-zinc-500 mt-1">Update the default password before going live.</p>
              <div className="space-y-4 mt-6">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1.5">Current password</label>
                  <input data-testid="current-password" type="password" value={pw.current_password}
                    onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-400" />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1.5">New password</label>
                  <input data-testid="new-password" type="password" value={pw.new_password}
                    onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-400" />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1.5">Confirm new password</label>
                  <input data-testid="confirm-password" type="password" value={pw.confirm}
                    onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-400" />
                </div>
                <button data-testid="change-password-button" onClick={changePassword} disabled={pwSaving}
                  className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60">
                  {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head">{editId ? "Edit product" : "New product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs text-zinc-500 mb-1.5">Title</label>
              <input data-testid="form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-400" />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1.5">Description</label>
              <textarea data-testid="form-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-400 resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-zinc-500 mb-1.5">Category</label>
                <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-400">
                  <option value="ebooks">E-Books</option>
                  <option value="templates">Templates</option>
                  <option value="courses">Courses</option>
                  <option value="bundles">Bundles</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1.5">Price (USD)</label>
                <input data-testid="form-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-400" />
              </div>
            </div>
            <Uploader label="Cover image" accept="image/*" testid="upload-cover"
              value={form.cover_path} fileName={form.cover_path && !form.cover_path.startsWith("http") ? "Cover uploaded ✓" : (form.cover_path ? "Current cover" : "")}
              onUploaded={(path) => setForm((f) => ({ ...f, cover_path: path }))} />
            <Uploader label="Downloadable product file" accept="*" testid="upload-file"
              value={form.file_path} fileName={form.file_name}
              onUploaded={(path, name) => setForm((f) => ({ ...f, file_path: path, file_name: name }))} />
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input data-testid="form-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
              Feature on homepage hero
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-zinc-700 text-sm hover:border-zinc-500 transition-colors">
              <X className="w-4 h-4 inline mr-1" /> Cancel
            </button>
            <button data-testid="save-product-button" onClick={save} disabled={saving}
              className="px-5 py-2 rounded-md bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : (editId ? "Update" : "Create")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
